import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, DatePicker, Button, Typography, Space, Divider, message, Spin } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, UserOutlined, HeartOutlined } from '@ant-design/icons';
import { COLORS } from '../../constants/colors';
import { ROUTES } from '../../constants/routes';
import PatientService from '../../services/PatientService';
import dayjs from 'dayjs';
import { Container, PageHeader, FormCard } from './styled';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PatientEdit = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [patient, setPatient] = useState(null);

  useEffect(() => {
    loadPatientData();
  }, [patientId]);

  const loadPatientData = async () => {
    try {
      setInitialLoading(true);
      const response = await PatientService.getPatientDetails(patientId);
      const patientData = response.data;
      setPatient(patientData);
      
      // Initialize form with patient data
      form.setFieldsValue({
        firstName: patientData.firstName,
        lastName: patientData.lastName,
        emailAddress: patientData.emailAddress || patientData.email,
        phoneNumber: patientData.phoneNumber || patientData.phone,
        dateOfBirth: patientData.dateOfBirth ? dayjs(patientData.dateOfBirth) : null,
        emergencyContact: patientData.emergencyContact,
        emergencyPhone: patientData.emergencyPhone,
        medicalConditions: patientData.medicalConditions,
        medications: patientData.medications,
        allergies: patientData.allergies
      });
    } catch (error) {
      console.error('Error loading patient data:', error);
      message.error('Failed to load patient data');
      navigate(ROUTES.ADMIN_PATIENT_QUEUE);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Convert date to ISO string
      const updatedData = {
        ...values,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null
      };

      await PatientService.updatePatient(patientId, updatedData);
      
      message.success('Patient details updated successfully');
      navigate(ROUTES.ADMIN_PATIENT_QUEUE);
    } catch (error) {
      console.error('Error updating patient:', error);
      message.error('Failed to update patient details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.ADMIN_PATIENT_QUEUE);
  };

  if (initialLoading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading patient data...</div>
        </div>
      </Container>
    );
  }

  if (!patient) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Text>Patient not found</Text>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            type="text"
          >
            Back to Intake
          </Button>
        </Space>        <Title level={2} style={{ margin: 0, color: COLORS.textDark }}>
          <UserOutlined style={{ color: COLORS.$alertRed, marginRight: 8 }} />
          Edit Patient Details
        </Title>
        <Text style={{ color: COLORS.textMuted }}>
          {patient.firstName} {patient.lastName}
        </Text>
      </PageHeader>

      <FormCard>
        <Form form={form} layout="vertical" size="large" autoComplete="off">
          <Title level={4} style={{ color: COLORS.textDark, marginBottom: 24 }}>
            Personal Information
          </Title>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              label="Email Address"
              name="emailAddress"
              rules={[
                { required: true, message: 'Please enter email address' },
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phoneNumber"
              rules={[{ required: true, message: 'Please enter phone number' }]}
            >
              <Input />
            </Form.Item>
          </div>

          <Form.Item
            label="Date of Birth"
            name="dateOfBirth"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Title level={5} style={{ color: COLORS.textDark, marginTop: 32, marginBottom: 16 }}>
            Emergency Contact
          </Title>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              label="Emergency Contact Name"
              name="emergencyContact"
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Emergency Phone"
              name="emergencyPhone"
            >
              <Input />
            </Form.Item>
          </div>

          <Divider />          <Title level={4} style={{ color: COLORS.textDark, marginBottom: 16 }}>
            <HeartOutlined style={{ color: COLORS.$alertRed, marginRight: 8 }} />
            Medical Information
          </Title>

          <Form.Item
            label="Medical Conditions"
            name="medicalConditions"
          >
            <TextArea rows={3} placeholder="Enter any known medical conditions" />
          </Form.Item>

          <Form.Item
            label="Current Medications"
            name="medications"
          >
            <TextArea rows={3} placeholder="Enter current medications" />
          </Form.Item>

          <Form.Item
            label="Allergies"
            name="allergies"
          >
            <TextArea rows={3} placeholder="Enter any known allergies" />
          </Form.Item>

          <div style={{ marginTop: 32, textAlign: 'right' }}>
            <Space>
              <Button size="large" onClick={handleCancel}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                size="large" 
                loading={loading} 
                onClick={handleSave}
                icon={<SaveOutlined />}
                style={{ backgroundColor: COLORS.$primaryBlue }}
              >
                Save Changes
              </Button>
            </Space>
          </div>
        </Form>
      </FormCard>
    </Container>
  );
};

export default PatientEdit;
